import { timeDay } from "d3";

import { accessors, LAYOUT, ATTRIBUTE_TYPE, ALL_WHITE_LINES } from ".";

/**
 * This function takes in an object of associations,
 * and flattens it into coordinates that are then consumed
 * by the rendering component
 */
// export const toAttributesDefaultConfig = {
//   colorFunc: accessors.color,
//   opacityFunc: accessors.opacity,
// };

export const attributesFromAssociations = ({
  associations,
  data,
  defaultWidth = {
    [ATTRIBUTE_TYPE.TARGETED]: LAYOUT.TIMELINE.SPHERES.SIZE, // radius
    [ATTRIBUTE_TYPE.GLOBAL]: LAYOUT.TIMELINE.LINES.GLOBAL.WIDTH,
    [ATTRIBUTE_TYPE.CONTEXTUAL]: LAYOUT.TIMELINE.LINES.CONTEXTUAL.WIDTH,
  },
  scales,
  fieldsByTarget = {},
  config = {
    colorFunc: accessors.color,
    opacityFunc: accessors.opacity,
  },
}) => {
  // Count width of one day (screen size) based on our scale
  const dayWidth =
    scales.time(timeDay.offset(scales.time.domain()[0], 1)) -
    scales.time(scales.time.domain()[0]);

  const fieldFromTargetDict = (name) => {
    return fieldsByTarget[name] || null;
  };

  // Which array ID does this element take in the attribute array?
  let attributeID = -1;

  const attributes = data.flatMap((d) => {
    const points = [];

    let color = config.colorFunc(d);
    let opacity = config.opacityFunc(d);
    let scaleX = 1;
    let x = scales.x(accessors.date.start(d));
    let y = 0;
    let z = 0;
    let size = 0.07;
    const id = accessors.id(d);

    // fuzziness is a numerical value —
    // its value extends the rendered element by [dayWidth] on either side.
    // eg. a fuzziness value of 180 will extend the shape 90 days
    // on either side.

    // if fuzziness value is 0, make it 1
    const fuzziness = accessors.date.fuzziness(d) || 1;
    const fuzzinessWidth = fuzziness * dayWidth; // in coordinates
    const renderFuzziness = accessors.date.fuzziness(d) !== 0;

    const isWhiteEvent = ALL_WHITE_LINES.indexOf(accessors.event.type(d)) >= 0;

    // we don't want w/ fuzziness width to be smaller than default width
    const targetedWidth = Math.max(
      fuzzinessWidth,
      defaultWidth[ATTRIBUTE_TYPE.TARGETED] * 2
    );
    let contextualWidth = Math.max(
      fuzzinessWidth,
      defaultWidth[ATTRIBUTE_TYPE.CONTEXTUAL]
    );
    const globalWidth = Math.max(
      fuzzinessWidth,
      defaultWidth[ATTRIBUTE_TYPE.GLOBAL]
    );

    if (isWhiteEvent) {
      // take down thickness of white events, as it can be overwhelming
      contextualWidth *= 0.5;
    }
    if (fuzziness > 0) {
      // take down opacity for fuzzy events
      opacity *= 0.5;
    }

    const p = {
      ...d,
      color,
      opacity,
      size,
      scaleX,
      height: 0,
      id, // this ID will be used for de-duplicating the card stack
      renderFuzziness,
      x,
      y,
      z,
    };

    const targets = accessors.target.name(d);

    // aka fields of operation
    const fields = accessors.association(d);

    const targetAssociations = accessors.target.association(d);

    if (targets && targets.length) {
      targets.forEach((t) => {
        // scaleX *= 1.2;

        // const tAssoc = fieldFromTargetDict(target);
        const tAssoc = fieldFromTargetDict(t);
        if (!tAssoc) return;

        tAssoc.forEach((ta) => {
          points.push({
            ...p,
            attributeID: ++attributeID,
            association: ta,
            type: ATTRIBUTE_TYPE.TARGETED,
            scaleX: scaleX * 1.2,
            width: targetedWidth,
            height: associations[ta].y.step(),
            y: associations[ta].y(t),
            z: scales.z(ta),
            target: { ...p.target, name: t },
          });
        });
      });
    }

    if (targetAssociations && targetAssociations.length) {
      targetAssociations.forEach((t, i) => {
        const _scaleX = scaleX * 0.7;

        // const tAssoc = fieldFromTargetDict(t);
        const tAssoc = fieldFromTargetDict(t);
        if (!tAssoc) return;

        tAssoc.forEach((ta) => {
          points.push({
            ...p,
            attributeID: ++attributeID,
            association: ta,
            type: ATTRIBUTE_TYPE.CONTEXTUAL,
            scaleX: _scaleX,
            width: contextualWidth,
            height: associations[ta].y.step(),
            y: associations[ta].y(t),
            z: scales.z(ta),
            target: { ...p.target, name: t },
          });

          if (!targets || !targets.length) {
            points.push({
              ...p,
              attributeID: ++attributeID,
              association: ta,
              type: ATTRIBUTE_TYPE.GLOBAL,
              // opacity: accessors.opacity[i],
              scaleX,
              width: globalWidth,
              height: associations[ta].y.step(),
              y: LAYOUT.TIMELINE.EVENTS.CONTEXTUAL.OFFSET,
              z: scales.z(ta),
              target: { ...p.target, name: null },
            });
          }
        });
      });
    }

    fields.forEach((field, i) => {
      const _scaleX = scaleX * 0.7;

      points.push({
        ...p,
        attributeID: ++attributeID,
        opacity: 0.8 * p.opacity * accessors.opacityPerAssociation(p)[i],
        association: field,
        type: ATTRIBUTE_TYPE.GLOBAL,
        // TODO This way, there isn't differential opacity per-field
        // opacity: accessors.opacity(d)[i],
        scaleX: _scaleX,
        width: globalWidth,
        height: associations[field].y.step(),
        y: LAYOUT.TIMELINE.EVENTS.CONTEXTUAL.OFFSET,
        z: scales.z(field),
        target: { ...p.target, name: null },
      });
    });
    if (!d.association.length) {
      if (!points.length) {
        // console.log(
        //   "Couldn't generate point for ",
        //   d.description,
        //   d.association
        // );
        console.warn("Couldn't render data point", d);
      } else {
        // console.log("[]", d.description, d);
      }
    }
    return points;
  });

  return attributes;
};
